import React from 'react';

import i18n from '@/utils/i18n';
import { useAppSelector } from '@/store';
import { selectOwnerProfilePrefs } from '@/store/slices/userPreferences';

import {
  submitContactRequest,
  type ContactContext,
  type ContactFormValues,
  type ContactRequestPayload,
  type ContactTopic,
} from './api';

const SENT_FLASH_MS = 4000;

/** Per-field validation messages (empty map = valid). */
export type ContactErrors = Partial<Record<keyof ContactFormValues, string>>;

const isBlank = (value: string): boolean => value.trim().length === 0;
// Pragmatic email shape check (matches the same "looks like an address" bar the
// rest of the app uses — the backend is the real authority).
const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/** Validate the owner-entered fields. Messages are built at call time so the i18n
 *  singleton reflects the active language. */
const validate = (values: ContactFormValues): ContactErrors => {
  const errors: ContactErrors = {};
  if (isBlank(values.name)) errors.name = i18n.t('Please enter your name');
  if (isBlank(values.email)) errors.email = i18n.t('Please enter your email address');
  else if (!isEmail(values.email)) errors.email = i18n.t('Please enter a valid email address');
  if (isBlank(values.subject)) errors.subject = i18n.t('Please add a subject');
  if (isBlank(values.message)) errors.message = i18n.t('Please write your message');
  else if (values.message.trim().length < 10) errors.message = i18n.t('Your message is a little too short');
  return errors;
};

/** Normalise an injected-config string to `null` when absent/empty. */
const orNull = (value: string | undefined): string | null => (value ? value : null);

export type ContactForm = {
  values: ContactFormValues;
  errors: ContactErrors;
  dirty: boolean;
  sending: boolean;
  sendError: string | null;
  justSent: boolean;
  onChange: (key: keyof ContactFormValues, value: string) => void;
  submit: () => void;
};

const EMPTY: ContactFormValues = { name: '', email: '', topic: 'general', subject: '', message: '' };

/**
 * Owns the Contact form: field state + validation + the (mocked) submit. Seeds
 * the name/email from the owner's saved profile and auth email so the form opens
 * pre-filled, and on submit assembles the {@link ContactContext} (selected brand,
 * tenant/site ids, locale, …) that rides along with the typed fields.
 */
export const useContactForm = (): ContactForm => {
  const accountEmail = useAppSelector((state) => state.authentication.user.email);
  const userId = useAppSelector((state) => state.authentication.user.user_id);
  const profile = useAppSelector(selectOwnerProfilePrefs);
  const brand = useAppSelector((state) => state.casinoInstance.activeBrand);

  // Seed name from the saved profile (first + last), email from the auth account.
  // Recomputed if those land after first render; merged below so it never clobbers
  // edits the owner has already made.
  const seeded = React.useMemo<ContactFormValues>(() => {
    const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
    return { ...EMPTY, name, email: accountEmail ?? '' };
  }, [profile?.firstName, profile?.lastName, accountEmail]);

  const [values, setValues] = React.useState<ContactFormValues>(seeded);
  const [touched, setTouched] = React.useState(false);
  const [errors, setErrors] = React.useState<ContactErrors>({});
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [justSent, setJustSent] = React.useState(false);

  const timer = React.useRef<number | null>(null);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  // Adopt the seeded name/email once they arrive, but only while the owner hasn't
  // typed yet, so late-loading prefs never overwrite in-progress input.
  React.useEffect(() => {
    if (!touched) setValues(seeded);
  }, [seeded, touched]);

  const dirty = touched && (values.name.trim() !== '' || values.subject.trim() !== '' || values.message.trim() !== '');

  const onChange = React.useCallback((key: keyof ContactFormValues, value: string) => {
    setTouched(true);
    setJustSent(false);
    // `topic` is a narrower union than string, but the Select only ever emits a
    // valid member, so the assertion is sound (same pattern as the Billing form).
    setValues((prev) => ({ ...prev, [key]: value }) as ContactFormValues);
    // Clear the edited field's error so a stale message doesn't linger as they fix it.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }, []);

  const buildContext = React.useCallback((): ContactContext => {
    return {
      userId: userId ?? null,
      accountEmail: accountEmail ?? null,
      brandId: brand?.id ?? null,
      brandName: brand?.casino_data?.details?.brand ?? null,
      brandTenantId: brand?.tenant_id ?? null,
      siteId: orNull(window.config.siteId),
      tenantId: orNull(window.config.clientId),
      locale: i18n.language,
      pageUrl: window.location.href,
      userAgent: window.navigator.userAgent,
      submittedAt: new Date().toISOString(),
    };
  }, [userId, accountEmail, brand]);

  const submit = React.useCallback(() => {
    const found = validate(values);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return; // block submit while any field is invalid

    const payload: ContactRequestPayload = { ...values, context: buildContext() };
    setSending(true);
    setSendError(null);
    void submitContactRequest(payload)
      .then(() => {
        if (!mounted.current) return;
        // Reset to a fresh (re-seeded) form and flash the success state for a moment.
        setValues(seeded);
        setTouched(false);
        setErrors({});
        setJustSent(true);
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          if (mounted.current) setJustSent(false);
        }, SENT_FLASH_MS);
      })
      .catch(() => {
        if (mounted.current) setSendError(i18n.t('Could not send your message. Please try again.'));
      })
      .finally(() => {
        if (mounted.current) setSending(false);
      });
  }, [values, buildContext, seeded]);

  return { values, errors, dirty, sending, sendError, justSent, onChange, submit };
};

/** Re-export so the section/page import the topic options from one place. */
export type { ContactTopic };
